import React, { useState, useRef } from 'react';
import { Row, Col, Button, Alert, Form, Card, Table, Badge, InputGroup, Dropdown } from 'react-bootstrap';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpload,
  faFileArrowUp,
  faSpinner,
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
  faEye,
  faEdit,
  faSave,
  faTimes,
  faFilePdf,
  faMagicWandSparkles,
  faBug,
  faEllipsisV,
  faRuler,
  faScaleBalanced,
  faEraser,
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { toast } from 'react-toastify';

import PrivateLayout from '../layouts/PrivateLayout';
import Panel from '../common/Panel';
import Pagination from '../common/Pagination';
import {
  useCreateDocuSealTemplate,
  useRegenerateDbqDebugArtifacts,
  useCalculateCharLimits,
  useCalculateMoCharLimit,
  useClearCharLimits,
} from '../../services/dbq';
import { useUser } from '../../contexts/UserContext';

const DbqWorkbench = () => {
  const navigate = useNavigate();
  const { user, hasPermission } = useUser();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadQueue, setUploadQueue] = useState([]); // [{ id, file, status, result, errorMessage }]
  const [autoCreateDocusealTemplates, setAutoCreateDocusealTemplates] = useState(true);
  const [isProcessingUploadQueue, setIsProcessingUploadQueue] = useState(false);
  const [uploadSummary, setUploadSummary] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [templateIdValue, setTemplateIdValue] = useState('');
  const [creatingTemplateForFormId, setCreatingTemplateForFormId] = useState(null); // Track which form is having template created
  const [regeneratingArtifactsForFormId, setRegeneratingArtifactsForFormId] = useState(null);
  const [calculatingCharLimitsForFormId, setCalculatingCharLimitsForFormId] = useState(null);
  const [calculatingMoCharLimitForFormId, setCalculatingMoCharLimitForFormId] = useState(null);
  const [clearingCharLimitsForFormId, setClearingCharLimitsForFormId] = useState(null);
  const fileInputRef = useRef(null);
  const templatePdfInputRefs = useRef({}); // Refs for template PDF inputs per form
  const debugArtifactsPdfInputRefs = useRef({}); // Refs for debug artifact regen PDF inputs per form
  const charLimitsPdfInputRefs = useRef({}); // Refs for char limits calculation PDF inputs per form
  const moCharLimitPdfInputRefs = useRef({}); // Refs for MO-only char limit calculation PDF inputs per form

  // Query for fetching DBQ forms list
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['dbqForms', page, search],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
      });
      if (search) {
        params.append('search', search);
      }
      const response = await axios.get(`/dbq/forms?${params}`);
      return response.data;
    },
  });

  // Mutation for uploading and parsing PDF
  const parseMutation = useMutation({
    mutationFn: async ({ file, createDocusealTemplate }) => {
      const formData = new FormData();
      formData.append('pdf', file);
      formData.append('createDocusealTemplate', createDocusealTemplate ? 'true' : 'false');

      const response = await axios.post('/dbq/parse', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
  });

  // Mutation for updating template ID
  const updateTemplateMutation = useMutation({
    mutationFn: async ({ dbqFormId, docusealTemplateId }) => {
      const response = await axios.put(`/dbq/forms/${dbqFormId}/template`, {
        docusealTemplateId,
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Template ID updated successfully');
      setEditingTemplateId(null);
      setTemplateIdValue('');
      refetch(); // Refresh the list
    },
    onError: (error) => {
      toast.error(error?.response?.data?.msg || 'Failed to update template ID');
    },
  });

  // Mutation for creating DocuSeal template from PDF
  const createTemplateMutation = useCreateDocuSealTemplate(
    (result) => {
      toast.success(`DocuSeal template created! ID: ${result.templateId} (${result.fieldsCreated} fields)`);
      setCreatingTemplateForFormId(null);
      refetch(); // Refresh the list to show the new template ID
    },
    (error) => {
      toast.error(error?.response?.data?.msg || 'Failed to create DocuSeal template');
      setCreatingTemplateForFormId(null);
    }
  );

  // Mutation for regenerating debug artifacts
  const regenerateArtifactsMutation = useRegenerateDbqDebugArtifacts(
    (result, variables) => {
      const dir = result?.debugArtifacts?.artifactsDir || result?.artifactsDir || result?.baseDir;
      const templateId = result?.docuseal?.templateId;
      const templateCreated = result?.docuseal?.created;
      const templateError = result?.docuseal?.error;

      if (templateCreated && templateId) {
        toast.success(`Template regenerated! ID: ${templateId}${dir ? ` • Artifacts: ${dir}` : ''}`);
      } else if (result?.docuseal?.attempted && !templateCreated) {
        toast.warning(`${templateError || 'Template regeneration failed'}${dir ? ` • Artifacts: ${dir}` : ''}`);
      } else {
        toast.success(dir ? `Regenerate complete • Artifacts: ${dir}` : 'Regenerate complete');
      }

      setRegeneratingArtifactsForFormId(null);
      refetch(); // refresh list to show updated templateId
    },
    (error, variables) => {
      const code = error?.response?.data?.code;
      const msg = error?.response?.data?.msg || 'Failed to regenerate debug artifacts';

      if (code === 'MISSING_SOURCE_PDF') {
        toast.info('No stored source PDF found. Please select the DBQ PDF to regenerate artifacts.');
        setRegeneratingArtifactsForFormId(null);
        debugArtifactsPdfInputRefs.current[variables?.dbqFormId]?.click();
        return;
      }

      toast.error(msg);
      setRegeneratingArtifactsForFormId(null);
    }
  );

  // Mutation for calculating character limits from PDF
  const calculateCharLimitsMutation = useCalculateCharLimits(
    (result) => {
      const fieldsUpdated = result?.fieldsUpdated || 0;
      if (fieldsUpdated > 0) {
        toast.success(`Character limits calculated! Updated ${fieldsUpdated} long text field${fieldsUpdated === 1 ? '' : 's'}`);
      } else {
        toast.info('No long text fields found to update');
      }
      setCalculatingCharLimitsForFormId(null);
      refetch(); // Refresh list to reflect updated formJson
    },
    (error) => {
      const msg = error?.response?.data?.msg || 'Failed to calculate character limits';
      toast.error(msg);
      setCalculatingCharLimitsForFormId(null);
    }
  );

  // Handle PDF file selection for template creation
  const handleTemplatePdfSelect = (dbqFormId, file) => {
    if (file && file.type === 'application/pdf') {
      setCreatingTemplateForFormId(dbqFormId);
      createTemplateMutation.mutate({ dbqFormId, pdfFile: file });
    } else {
      toast.error('Please select a valid PDF file');
    }
  };

  // Handle regenerating debug artifacts (uses stored PDF when available)
  const handleRegenerateArtifacts = (dbqFormId) => {
    setRegeneratingArtifactsForFormId(dbqFormId);
    regenerateArtifactsMutation.mutate({ dbqFormId });
  };

  // Handle regenerating debug artifacts using an uploaded PDF
  const handleRegenerateArtifactsWithPdf = (dbqFormId, file) => {
    if (file && file.type === 'application/pdf') {
      setRegeneratingArtifactsForFormId(dbqFormId);
      regenerateArtifactsMutation.mutate({ dbqFormId, pdfFile: file });
    } else {
      toast.error('Please select a valid PDF file');
    }
  };

  // Handle calculating character limits using an uploaded PDF
  const handleCalculateCharLimits = (dbqFormId, file) => {
    if (file && file.type === 'application/pdf') {
      setCalculatingCharLimitsForFormId(dbqFormId);
      calculateCharLimitsMutation.mutate({ dbqFormId, pdfFile: file });
    } else {
      toast.error('Please select a valid PDF file');
    }
  };

  // Mutation for calculating the MO/Remarks field's char limit and migrating affected DBQs
  const calculateMoCharLimitMutation = useCalculateMoCharLimit(
    (result) => {
      const max = result?.maxCharCount;
      const scanned = result?.migration?.scanned ?? 0;
      const truncated = result?.migration?.truncated ?? 0;
      const skipped = Math.max(scanned - truncated, 0);
      const parts = [];
      if (Number.isFinite(max)) {
        parts.push(`MO limit set: ${max} chars`);
      } else {
        parts.push('MO limit set');
      }
      parts.push(`Migrated ${truncated} of ${scanned} DBQs (${skipped} already within limit)`);
      toast.success(parts.join('. ') + '.');
      setCalculatingMoCharLimitForFormId(null);
      refetch();
    },
    (error) => {
      const msg = error?.response?.data?.msg || 'Failed to calculate MO field character limit';
      toast.error(msg);
      setCalculatingMoCharLimitForFormId(null);
    }
  );

  const handleCalculateMoCharLimit = (dbqFormId, file) => {
    if (file && file.type === 'application/pdf') {
      setCalculatingMoCharLimitForFormId(dbqFormId);
      calculateMoCharLimitMutation.mutate({ dbqFormId, pdfFile: file });
    } else {
      toast.error('Please select a valid PDF file');
    }
  };

  // Mutation for clearing all character limits from long_text fields on a form
  const clearCharLimitsMutation = useClearCharLimits(
    (result) => {
      const fieldsCleared = result?.fieldsCleared || 0;
      if (fieldsCleared > 0) {
        toast.success(`Cleared character limits from ${fieldsCleared} field${fieldsCleared === 1 ? '' : 's'}`);
      } else {
        toast.info('No character limits were set on this form');
      }
      setClearingCharLimitsForFormId(null);
      refetch();
    },
    (error) => {
      const msg = error?.response?.data?.msg || 'Failed to clear character limits';
      toast.error(msg);
      setClearingCharLimitsForFormId(null);
    }
  );

  const handleClearCharLimits = (dbqFormId) => {
    if (!window.confirm('Clear all character limits from every long-text field on this form? You can re-run Calculate Char Limits afterward.')) {
      return;
    }
    setClearingCharLimitsForFormId(dbqFormId);
    clearCharLimitsMutation.mutate({ dbqFormId });
  };

  const updateQueueItem = (id, patch) => {
    setUploadQueue((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  };

  const buildQueueItemId = (file) =>
    `${file.name}__${file.size}__${file.lastModified}__${Math.random().toString(36).slice(2)}`;

  const handleFilesSelect = (fileList) => {
    const files = Array.from(fileList || []);
    const pdfs = files.filter((f) => f && f.type === 'application/pdf');
    const nonPdfs = files.filter((f) => f && f.type !== 'application/pdf');

    if (nonPdfs.length > 0) {
      toast.error('Only PDF files are allowed');
    }

    if (pdfs.length === 0) return;

    setUploadSummary(null);
    setSelectedFiles(pdfs);
    setUploadQueue(
      pdfs.map((file) => ({
        id: buildQueueItemId(file),
        file,
        status: 'ready', // ready | processing | success | warning | error
        result: null,
        errorMessage: null,
      }))
    );
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files?.length) handleFilesSelect(files);
    // Allow re-selecting the same file(s)
    e.target.value = '';
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files?.length) handleFilesSelect(files);
  };

  const handleClearUploadQueue = () => {
    if (isProcessingUploadQueue) return;
    setSelectedFiles([]);
    setUploadQueue([]);
    setUploadSummary(null);
  };

  const handleProcessUploadQueue = async () => {
    if (isProcessingUploadQueue) return;
    if (uploadQueue.length === 0) return;

    setUploadSummary(null);
    setIsProcessingUploadQueue(true);

    let parsedOk = 0;
    let parsedFailed = 0;
    let templateCreated = 0;
    let templateFailed = 0;
    let templateSkipped = 0;
    let collisions = 0;

    const itemsToProcess = [...uploadQueue];

    for (const item of itemsToProcess) {
      updateQueueItem(item.id, { status: 'processing', result: null, errorMessage: null });

      try {
        const result = await parseMutation.mutateAsync({
          file: item.file,
          createDocusealTemplate: autoCreateDocusealTemplates,
        });

        parsedOk += 1;

        const docuseal = result?.docuseal;
        const templateAttempted = !!docuseal?.attempted;
        const templateOk = !!docuseal?.created;

        if (!templateAttempted) templateSkipped += 1;
        else if (templateOk) templateCreated += 1;
        else templateFailed += 1;

        if (result?.collision) collisions += 1;

        const finalStatus =
          templateAttempted && !templateOk ? 'warning' : 'success';

        updateQueueItem(item.id, {
          status: finalStatus,
          result,
          errorMessage: null,
        });
      } catch (err) {
        parsedFailed += 1;
        updateQueueItem(item.id, {
          status: 'error',
          result: null,
          errorMessage:
            err?.response?.data?.msg || err?.message || 'Failed to parse DBQ PDF',
        });
      }
    }

    setIsProcessingUploadQueue(false);

    setUploadSummary({
      total: itemsToProcess.length,
      parsedOk,
      parsedFailed,
      templateCreated,
      templateFailed,
      templateSkipped,
      collisions,
    });

    refetch();
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleViewForm = (form) => {
    navigate(`/dbq/${encodeURIComponent(form.dbqFormId)}`);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleEditTemplateId = (form) => {
    setEditingTemplateId(form.dbqFormId);
    setTemplateIdValue(form.docusealTemplateId || '');
  };

  const handleSaveTemplateId = (dbqFormId) => {
    updateTemplateMutation.mutate({
      dbqFormId,
      docusealTemplateId: templateIdValue,
    });
  };

  const handleCancelEditTemplateId = () => {
    setEditingTemplateId(null);
    setTemplateIdValue('');
  };

  return (
    <PrivateLayout>
      <Panel className="pb-3 mb-3">
        <Row>
          <Col xs={12} md={8}>
            <h2 className="mb-3">DBQ Form Library</h2>
            <p className="text-muted">
              Upload and manage VA Disability Benefits Questionnaires
            </p>
          </Col>
          <Col xs={12} md={4} className="text-md-end">
            <div className="d-flex flex-wrap gap-2 justify-content-md-end">
              {hasPermission('admin:dbq_forms') && (
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/dbq/toe')}
                >
                  <FontAwesomeIcon icon={faScaleBalanced} className="me-2" />
                  Manage TOEs
                </Button>
              )}
              <Button
                variant="primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={isProcessingUploadQueue}
              >
                <FontAwesomeIcon icon={faFileArrowUp} className="me-2" />
                Select DBQ PDF(s)
              </Button>
            </div>
            <Form.Control
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              multiple
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
          </Col>
        </Row>

        <hr />

        {/* Drag & drop zone */}
        <Row className="mb-3">
          <Col xs={12}>
            <Card
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              onClick={() => {
                if (!isProcessingUploadQueue) fileInputRef.current?.click();
              }}
              className={dragActive ? 'border-primary' : ''}
              style={{
                border: '2px dashed rgba(0,0,0,0.2)',
                cursor: isProcessingUploadQueue ? 'not-allowed' : 'pointer',
                opacity: isProcessingUploadQueue ? 0.6 : 1,
              }}
            >
              <Card.Body className="text-center">
                <FontAwesomeIcon icon={faFileArrowUp} size="2x" className="mb-2 text-muted" />
                <div className="fw-semibold">Drag & drop DBQ PDF(s) here, or click to select</div>
                <div className="text-muted small mt-1">
                  One upload will parse the DBQ and (optionally) auto-create the DocuSeal template.
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Upload Queue */}
        {uploadQueue.length > 0 && (
          <Row className="mb-3">
            <Col xs={12}>
              <Card>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                    <div>
                      <h6 className="mb-1">Upload Queue ({uploadQueue.length})</h6>
                      <Form.Check
                        type="switch"
                        id="dbq-auto-create-docuseal"
                        label="Auto-create DocuSeal template (recommended)"
                        checked={autoCreateDocusealTemplates}
                        onChange={(e) => setAutoCreateDocusealTemplates(e.target.checked)}
                        disabled={isProcessingUploadQueue}
                      />
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleClearUploadQueue}
                        disabled={isProcessingUploadQueue}
                      >
                        Clear
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleProcessUploadQueue}
                        disabled={isProcessingUploadQueue}
                      >
                        {isProcessingUploadQueue ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faUpload} className="me-2" />
                            Process {uploadQueue.length} file{uploadQueue.length === 1 ? '' : 's'}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="mt-3">
                    <Table hover responsive size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>File</th>
                          <th>Status</th>
                          <th>Parsed Form</th>
                          <th>DocuSeal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadQueue.map((item) => {
                          const result = item.result;
                          const formId = result?.form?.id;
                          const dbqFormId = result?.dbqFormId;
                          const docuseal = result?.docuseal;

                          const renderStatus = () => {
                            if (item.status === 'processing') {
                              return (
                                <Badge bg="info">
                                  <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                  Processing
                                </Badge>
                              );
                            }
                            if (item.status === 'success') {
                              return (
                                <Badge bg="success">
                                  <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                  Done
                                </Badge>
                              );
                            }
                            if (item.status === 'warning') {
                              return (
                                <Badge bg="warning" text="dark">
                                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                                  Done (template issue)
                                </Badge>
                              );
                            }
                            if (item.status === 'error') {
                              return (
                                <Badge bg="danger">
                                  <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                                  Failed
                                </Badge>
                              );
                            }
                            return <Badge bg="secondary">Ready</Badge>;
                          };

                          return (
                            <tr key={item.id}>
                              <td>
                                <strong>{item.file.name}</strong>
                                <div className="text-muted small">{formatFileSize(item.file.size)}</div>
                              </td>
                              <td style={{ minWidth: 190 }}>{renderStatus()}</td>
                              <td style={{ minWidth: 220 }}>
                                {item.status === 'error' ? (
                                  <span className="text-danger small">{item.errorMessage}</span>
                                ) : formId ? (
                                  <>
                                    <div className="small">
                                      <span className="text-muted">formId: </span>
                                      <code>{formId}</code>
                                    </div>
                                    <div className="small">
                                      <span className="text-muted">dbqFormId: </span>
                                      <code>{dbqFormId}</code>
                                    </div>
                                    {result?.collision && (
                                      <div className="small text-muted mt-1">
                                        Collision: saved as <code>{formId}</code> (original <code>{result.originalFormId}</code>)
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td style={{ minWidth: 260 }}>
                                {!result ? (
                                  autoCreateDocusealTemplates ? (
                                    <span className="text-muted small">Will create</span>
                                  ) : (
                                    <span className="text-muted small">Will skip</span>
                                  )
                                ) : docuseal?.created ? (
                                  <div className="small">
                                    <span className="text-muted">templateId: </span>
                                    <code>{docuseal.templateId}</code>
                                    <div className="text-muted">
                                      {docuseal.fieldsCreated} fields ({docuseal.pdfFieldsExtracted} PDF widgets)
                                    </div>
                                  </div>
                                ) : docuseal?.attempted ? (
                                  <span className="text-warning small">
                                    {docuseal.error || 'Template creation failed'}
                                  </span>
                                ) : docuseal?.attempted === false ? (
                                  <span className="text-muted small">
                                    Skipped
                                    {docuseal?.skippedReason ? ` (${docuseal.skippedReason})` : ''}
                                  </span>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Batch summary */}
        {uploadSummary && (
          <Alert
            variant={
              uploadSummary.parsedFailed > 0
                ? 'danger'
                : uploadSummary.templateFailed > 0
                  ? 'warning'
                  : 'success'
            }
            className="mb-3"
            dismissible
            onClose={() => setUploadSummary(null)}
          >
            <strong>Upload complete.</strong>{' '}
            {uploadSummary.parsedOk}/{uploadSummary.total} parsed successfully.
            {uploadSummary.templateCreated +
              uploadSummary.templateFailed +
              uploadSummary.templateSkipped >
              0 && (
              <>
                {' '}
                Templates: {uploadSummary.templateCreated} created, {uploadSummary.templateFailed} failed, {uploadSummary.templateSkipped} skipped.
              </>
            )}
            {uploadSummary.collisions > 0 && (
              <>
                {' '}
                Collisions: {uploadSummary.collisions} (saved with a new form ID to avoid overwriting).
              </>
            )}
          </Alert>
        )}

        {/* Search */}
        <Row className="mb-3">
          <Col xs={12} md={6}>
            <form onSubmit={handleSearchSubmit}>
              <Form.Control
                type="search"
                placeholder="Search by title or form ID..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </form>
          </Col>
        </Row>

        {/* Forms List */}
        {isLoading ? (
          <div className="text-center py-5">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            <p className="mt-3 text-muted">Loading forms...</p>
          </div>
        ) : data?.forms?.length === 0 ? (
          <Alert variant="info">
            <p className="mb-0">
              No DBQ forms found. Upload your first DBQ PDF to get started!
            </p>
          </Alert>
        ) : (
          <>
            <Table hover responsive>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Form ID</th>
                  <th>DocuSeal Template ID</th>
                  <th>Uploaded</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data?.forms?.map((form) => (
                  <tr key={form.dbqFormId}>
                    <td>
                      <strong>{form.title}</strong>
                      {form.shortName && (
                        <div className="text-muted small">{form.shortName}</div>
                      )}
                    </td>
                    <td>
                      <code className="small">{form.formId}</code>
                    </td>
                    <td style={{ minWidth: '320px' }}>
                      {editingTemplateId === form.dbqFormId ? (
                        <InputGroup size="sm">
                          <Form.Control
                            type="text"
                            value={templateIdValue}
                            onChange={(e) => setTemplateIdValue(e.target.value)}
                            placeholder="Enter template ID"
                            disabled={updateTemplateMutation.isPending}
                          />
                          <Button
                            variant="success"
                            onClick={() => handleSaveTemplateId(form.dbqFormId)}
                            disabled={updateTemplateMutation.isPending}
                          >
                            <FontAwesomeIcon icon={faSave} />
                          </Button>
                          <Button
                            variant="secondary"
                            onClick={handleCancelEditTemplateId}
                            disabled={updateTemplateMutation.isPending}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </Button>
                        </InputGroup>
                      ) : (
                        <div className="d-flex flex-column gap-2">
                          {/* Existing template ID display/edit */}
                          {form.docusealTemplateId ? (
                            <div className="d-flex align-items-center gap-2">
                              <code className="small">{form.docusealTemplateId}</code>
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0"
                                onClick={() => handleEditTemplateId(form)}
                                title="Edit template ID"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </Button>
                            </div>
                          ) : (
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              {/* Auto-create template from PDF button */}
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => templatePdfInputRefs.current[form.dbqFormId]?.click()}
                                disabled={creatingTemplateForFormId === form.dbqFormId}
                                title="Upload PDF to auto-create DocuSeal template"
                              >
                                {creatingTemplateForFormId === form.dbqFormId ? (
                                  <>
                                    <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                    Creating...
                                  </>
                                ) : (
                                  <>
                                    <FontAwesomeIcon icon={faMagicWandSparkles} className="me-1" />
                                    Create Template
                                  </>
                                )}
                              </Button>
                              <Form.Control
                                ref={(el) => (templatePdfInputRefs.current[form.dbqFormId] = el)}
                                type="file"
                                accept="application/pdf"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleTemplatePdfSelect(form.dbqFormId, file);
                                  }
                                  e.target.value = ''; // Reset input
                                }}
                                style={{ display: 'none' }}
                              />
                              {/* Manual template ID entry */}
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handleEditTemplateId(form)}
                                title="Manually enter template ID"
                              >
                                <FontAwesomeIcon icon={faEdit} className="me-1" />
                                Manual
                              </Button>
                            </div>
                          )}
                          
                          {/* Re-create template option when one already exists */}
                          {form.docusealTemplateId && (
                            <div>
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => templatePdfInputRefs.current[form.dbqFormId]?.click()}
                                disabled={creatingTemplateForFormId === form.dbqFormId}
                                title="Upload new PDF to recreate DocuSeal template"
                              >
                                {creatingTemplateForFormId === form.dbqFormId ? (
                                  <>
                                    <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                    Recreating...
                                  </>
                                ) : (
                                  <>
                                    <FontAwesomeIcon icon={faFilePdf} className="me-1" />
                                    Recreate Template
                                  </>
                                )}
                              </Button>
                              <Form.Control
                                ref={(el) => (templatePdfInputRefs.current[form.dbqFormId] = el)}
                                type="file"
                                accept="application/pdf"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleTemplatePdfSelect(form.dbqFormId, file);
                                  }
                                  e.target.value = ''; // Reset input
                                }}
                                style={{ display: 'none' }}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      <div>{moment(form.uploadedAt).format('MMM D, YYYY')}</div>
                      <div className="text-muted small">
                        {moment(form.uploadedAt).format('h:mm A')}
                      </div>
                    </td>
                    <td className="text-end">
                      <div className="d-inline-flex gap-2 align-items-center">
                        <Dropdown align="end">
                          <Dropdown.Toggle
                            variant="outline-primary"
                            size="sm"
                            id={`actions-dropdown-${form.dbqFormId}`}
                            disabled={
                              regeneratingArtifactsForFormId === form.dbqFormId ||
                              calculatingCharLimitsForFormId === form.dbqFormId ||
                              calculatingMoCharLimitForFormId === form.dbqFormId
                            }
                          >
                            {(regeneratingArtifactsForFormId === form.dbqFormId ||
                              calculatingCharLimitsForFormId === form.dbqFormId ||
                              calculatingMoCharLimitForFormId === form.dbqFormId) ? (
                              <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                            ) : null}
                            Actions
                          </Dropdown.Toggle>

                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleViewForm(form)}>
                              <FontAwesomeIcon icon={faEye} className="me-2" fixedWidth />
                              View
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => navigate(`/dbq/${form.dbqFormId}/debug`)}>
                              <FontAwesomeIcon icon={faBug} className="me-2" fixedWidth />
                              Debug Template
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item
                              onClick={() => handleRegenerateArtifacts(form.dbqFormId)}
                              disabled={regeneratingArtifactsForFormId === form.dbqFormId}
                            >
                              <FontAwesomeIcon icon={faMagicWandSparkles} className="me-2" fixedWidth />
                              {regeneratingArtifactsForFormId === form.dbqFormId
                                ? 'Regenerating...'
                                : 'Regenerate Artifacts'}
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() => charLimitsPdfInputRefs.current[form.dbqFormId]?.click()}
                              disabled={calculatingCharLimitsForFormId === form.dbqFormId}
                            >
                              <FontAwesomeIcon icon={faRuler} className="me-2" fixedWidth />
                              {calculatingCharLimitsForFormId === form.dbqFormId
                                ? 'Calculating...'
                                : 'Calculate Char Limits'}
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() => moCharLimitPdfInputRefs.current[form.dbqFormId]?.click()}
                              disabled={calculatingMoCharLimitForFormId === form.dbqFormId}
                            >
                              <FontAwesomeIcon icon={faScaleBalanced} className="me-2" fixedWidth />
                              {calculatingMoCharLimitForFormId === form.dbqFormId
                                ? 'Calculating MO limit...'
                                : 'Calculate MO Field Char Limit'}
                            </Dropdown.Item>
                            <Dropdown.Item
                              onClick={() => handleClearCharLimits(form.dbqFormId)}
                              disabled={clearingCharLimitsForFormId === form.dbqFormId}
                            >
                              <FontAwesomeIcon icon={faEraser} className="me-2" fixedWidth />
                              {clearingCharLimitsForFormId === form.dbqFormId
                                ? 'Clearing...'
                                : 'Clear All Character Limits'}
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>

                        {/* Hidden file inputs for various actions */}
                        <Form.Control
                          ref={(el) => (debugArtifactsPdfInputRefs.current[form.dbqFormId] = el)}
                          type="file"
                          accept="application/pdf"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleRegenerateArtifactsWithPdf(form.dbqFormId, file);
                            }
                            e.target.value = '';
                          }}
                          style={{ display: 'none' }}
                        />
                        <Form.Control
                          ref={(el) => (charLimitsPdfInputRefs.current[form.dbqFormId] = el)}
                          type="file"
                          accept="application/pdf"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleCalculateCharLimits(form.dbqFormId, file);
                            }
                            e.target.value = '';
                          }}
                          style={{ display: 'none' }}
                        />
                        <Form.Control
                          ref={(el) => (moCharLimitPdfInputRefs.current[form.dbqFormId] = el)}
                          type="file"
                          accept="application/pdf"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              handleCalculateMoCharLimit(form.dbqFormId, file);
                            }
                            e.target.value = '';
                          }}
                          style={{ display: 'none' }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Pagination */}
            {data?.pagination && data.pagination.totalPages > 1 && (
              <Pagination
                page={page}
                totalRows={data.pagination.total}
                rowsPerPage={data.pagination.limit}
                onBack={() => setPage(page - 1)}
                onForward={() => setPage(page + 1)}
              />
            )}
          </>
        )}
      </Panel>
    </PrivateLayout>
  );
};

export default DbqWorkbench;
